import { Form, Formik, FormikHelpers } from "formik";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import LoadingIndicator from "src/components/LoadingIndicator";
import { useSubaccount } from "src/components/SubaccountProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { Role } from "src/graphql/generated/globalTypes";

import { InviteError, InviteResult } from "./types";
import useRoleSelector, { FormikRoleSelector } from "./useRoleSelector";

const schema = yup.object().shape({
  fullName: yup.string().required("Required"),
  role: yup.string().required("Required"),
  email: yup.string().required("Required").email("Invalid email")
});

export type Values = { fullName: string; email: string; role: Role };

const useStyles = makeStyles(theme => ({
  errorAlert: {
    marginBottom: theme.spacing(3)
  },
  content: {
    width: "600px"
  }
}));

function ServerErrorMessage({
  error,
  submittedValues
}: {
  error: InviteError;
  submittedValues: Values;
}) {
  const { subaccountLink } = useSubaccount()!;
  switch (error) {
    case "ADMIN_ALREADY_EXISTS":
      return (
        <span>
          An admin with that email ({submittedValues.email}) already exists.
        </span>
      );
    case "DELETED_ADMIN_ALREADY_EXISTS":
      return (
        <span>
          A deactivated admin with that email ({submittedValues.email}) already
          exists. To add them back to your account, you can reactivate them from
          the{" "}
          <a
            href={`${subaccountLink(
              "/admins"
            )}?status=deactivated&search=${encodeURIComponent(
              submittedValues.email
            )}`}
            target="_blank"
          >
            deactivated users list
          </a>
          .
        </span>
      );
    case "UNKNOWN":
      return (
        <span>
          An error occurred while sending the invite. Please try again.
        </span>
      );
  }
}

export default function InviteDialog({
  open,
  onClose,
  handleInvite
}: {
  open: boolean;
  onClose: () => void;
  handleInvite: (values: Values) => Promise<InviteResult>;
}): JSX.Element {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { defaultRoleSelection, roleSelections } = useRoleSelector();

  const [serverError, setServerError] = useState<InviteError | null>(null);
  const [submittedValues, setSubmittedValues] = useState<Values | null>(null);

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    setServerError(null);
    setSubmittedValues(values);
    const result = await handleInvite(values);

    if (result.ok) {
      snackbar.enqueueSnackbar("Invite sent", { variant: "info" });
      helpers.resetForm();
      onClose();
    } else {
      setServerError(result.error);
    }
  }
  return (
    <Dialog open={open} aria-labelledby="alert-dialog-title">
      <Formik
        initialValues={{
          fullName: "",
          email: "",
          role: defaultRoleSelection as Role
        }}
        onSubmit={handleSubmit}
        validationSchema={schema}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogTitle id="alert-dialog-title">Invite Admin</DialogTitle>
            <DialogContent className={classes.content}>
              {isSubmitting ? (
                <LoadingIndicator />
              ) : (
                <>
                  {serverError && (
                    <Alert severity="error" className={classes.errorAlert}>
                      <ServerErrorMessage
                        error={serverError}
                        submittedValues={submittedValues!}
                      />
                    </Alert>
                  )}
                  <FormikErrorBanner />
                  <FormikTextField
                    fullWidth
                    label="Full Name"
                    name="fullName"
                  />
                  <FormikTextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                  />
                  <FormikRoleSelector
                    roleSelections={roleSelections}
                    name="role"
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={() => {
                  setServerError(null);
                  onClose();
                }}
                color="primary"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color={"primary"}
                variant="contained"
              >
                {isSubmitting ? "Sending..." : "Send Invite"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
